import { useState } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { formatMoney, formatNumber } from "@/lib/format";
import type { MatchedInterest } from "@/types/domain";

/** Fila etiqueta → valor del detalle; se omite cuando no hay dato. */
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] text-slate-400">{label}</span>
      <span className="text-right text-[12px] tabular-nums">{value}</span>
    </div>
  );
}

/** Matches visibles antes de tener que pedir el resto. */
export const VISIBLE_MATCHES = 10;

/**
 * Separador con el que el import de contrataciones menores une los tres textos
 * del ítem (`ítem — CUBSO — descripción de la licitación`). Es un em dash porque
 * no aparece en los textos del SEACE: se midió sobre 2250 campos y da cero.
 */
const MINOR_SEPARATOR = " — ";

/** Los fragmentos del texto, tal como los unió el import. */
export function descriptionParts(
  description: string,
  isMinor: boolean,
): string[] {
  return isMinor ? description.split(MINOR_SEPARATOR) : [description];
}

// Espejo de `keywordVariants` (backend/src/services/keywordmatch.service.ts): el
// match real busca la keyword tal cual Y sin tildes. Si acá solo buscáramos la
// forma con tilde, "rápida" no se resaltaría sobre "RAPIDA" y parecería un bug.
// Si cambia el original, actualizar este espejo.
const ACCENTED = "áéíóúüÁÉÍÓÚÜ";
const PLAIN = "aeiouuAEIOUU";

/**
 * Las formas con las que se buscó la keyword. NO se hace trim: un espacio al
 * inicio o al final es intencional ("NiFF " no debe matchear "NIFFX") y el
 * matcher lo respeta.
 */
function keywordVariants(keyword: string): string[] {
  if (!keyword) return [];
  const plain = [...keyword]
    .map((char) => {
      const index = ACCENTED.indexOf(char);
      return index === -1 ? char : PLAIN[index]!;
    })
    .join("");
  return plain === keyword ? [keyword] : [keyword, plain];
}

function contains(text: string, keyword: string): boolean {
  const haystack = text.toLowerCase();
  return keywordVariants(keyword).some(
    (variant) => variant.length > 0 && haystack.includes(variant.toLowerCase()),
  );
}

/**
 * Texto visible de la píldora: solo el fragmento que contiene la palabra del
 * perfil. Los otros dos son contexto (la descripción de la licitación además se
 * repite en cada ítem y ya se lee en la misma fila), y quedan en el hover.
 * Aplica únicamente a contrataciones menores: son las únicas cuyo texto viene
 * concatenado.
 */
export function pillText(
  description: string,
  keyword: string,
  isMinor: boolean,
): string {
  if (!isMinor) return description;

  const parts = descriptionParts(description, true);
  const matched = parts.filter((part) => contains(part, keyword));
  // Sin coincidencia textual (p. ej. el match vino por otra variante) se cae al
  // primer fragmento, que es la descripción del ítem.
  return (matched.length > 0 ? matched : parts.slice(0, 1))
    .join(MINOR_SEPARATOR)
    .trim();
}

/** Algunos textos del OECE traen el salto de línea escapado (un "\n" literal): se lee como espacio. */
const withoutEscapedBreaks = (text: string) =>
  text.replace(/\\[nrt]/g, " ").replace(/\s+/g, " ").trim();

/** Resalta las apariciones de la palabra del perfil dentro del texto. */
export function highlight(text: string, keyword: string): React.ReactNode {
  const variants = keywordVariants(keyword).filter((v) => v.length > 0);
  if (variants.length === 0) return text;

  // La keyword la escribe el usuario: se escapa antes de armar el patrón.
  const pattern = variants
    .map((variant) => variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const lowered = variants.map((variant) => variant.toLowerCase());

  return text
    .split(new RegExp(`(${pattern})`, "gi"))
    .map((chunk, index) =>
      lowered.includes(chunk.toLowerCase()) ? (
        <strong key={index} className="font-extrabold text-emerald-800 dark:text-emerald-300">
          {chunk}
        </strong>
      ) : (
        chunk
      ),
    );
}

/**
 * Ítem coincidente del listado: píldora con el nombre completo y su monto, y un
 * hover con el detalle (producto del perfil, cantidad, precio unitario).
 */
function MatchPill({
  match,
  currencySymbol,
  isMinor,
  compact = false,
}: {
  match: MatchedInterest;
  currencySymbol: string;
  isMinor: boolean;
  /** Un punto más chica, para la tarjeta del tablero. */
  compact?: boolean;
}) {
  // "Manual" es el centinela del backend para un ítem agregado a mano (sin
  // keyword): resaltarlo pintaría cualquier "manual" que traiga la descripción.
  const keyword = match.keyword === "Manual" ? "" : match.keyword;
  const description = withoutEscapedBreaks(match.description);

  const quantity =
    match.quantity != null
      ? `${formatNumber(match.quantity)}${match.unit ? ` ${match.unit}` : ""}`
      : null;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className={cn(
            "flex w-full cursor-pointer items-start gap-1.5 rounded-lg bg-emerald-500/10 font-semibold leading-tight text-emerald-700 transition-colors hover:bg-emerald-500/20 dark:text-emerald-400",
            compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-[11px]",
          )}
        >
          <span className="mt-1 size-1 shrink-0 rounded-full bg-emerald-500" />
          <span className="min-w-0 flex-1 break-words">
            {highlight(pillText(description, keyword, isMinor), keyword)}
          </span>
          {/* Un match puede no traer importe (convocatoria sin montos por
              ítem): un guion se lee mejor que "S/ 0". */}
          <span className="shrink-0 font-extrabold tabular-nums">
            {match.amount > 0
              ? formatMoney(match.amount, currencySymbol)
              : `${currencySymbol} -`}
          </span>
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="w-80 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* El texto completo, por fragmentos: en una menor son la descripción
            del ítem, el nombre del CUBSO y la de la licitación, y leerlos
            separados es más claro que un bloque unido por guiones. */}
        {!isMinor ? (
          <p className="text-[13px] font-bold leading-snug">
            {highlight(description, keyword)}
          </p>
        ) : (
        <div className="space-y-1.5">
          {descriptionParts(description, true).map((part, i) => (
            <p
              key={i}
              className={cn(
                "leading-snug",
                contains(part, keyword)
                  ? "text-[13px] font-bold"
                  : "text-[12px] text-slate-500 dark:text-slate-400",
              )}
            >
              {highlight(part, keyword)}
            </p>
          ))}
        </div>
        )}
        <div className="mt-3 space-y-1.5 border-t pt-3">
          <DetailRow
            label="Tu producto"
            value={
              match.keyword ? (
                <span className="font-bold text-emerald-600 dark:text-emerald-400">
                  {match.keyword}
                </span>
              ) : null
            }
          />
          <DetailRow label="Cantidad" value={quantity} />
          <DetailRow
            label="P. unitario"
            value={
              match.unitPrice != null
                ? formatMoney(match.unitPrice, currencySymbol, true)
                : null
            }
          />
          <DetailRow
            label="Monto ítem"
            value={
              match.amount > 0 ? (
                <span className="font-bold">
                  {formatMoney(match.amount, currencySymbol, true)}
                </span>
              ) : null
            }
          />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Lista de ítems coincidentes de una fila (listado de licitaciones y de empresas).
 * Se muestran todos y sin truncar: la oportunidad es lo que el usuario vino a ver.
 */
export function MatchList({
  matches,
  currencySymbol = "S/",
  className,
  isMinor = false,
  compact = false,
}: {
  matches: MatchedInterest[];
  currencySymbol?: string;
  className?: string;
  /** Recorta la descripción de la licitación, que se repite en cada ítem. */
  isMinor?: boolean;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (matches.length === 0) return null;

  // Una compra de ferretería trae hasta 31 ítems y todos matchean: se muestran
  // los primeros y el resto queda a un clic, sin esconder ninguno.
  const visible =
    !isMinor || expanded ? matches : matches.slice(0, VISIBLE_MATCHES);
  const hidden = matches.length - visible.length;

  return (
    <div className={cn("flex w-full flex-col items-start gap-1", className)}>
      {visible.map((m, i) => (
        <MatchPill
          key={`${m.description}-${i}`}
          match={m}
          currencySymbol={currencySymbol}
          isMinor={isMinor}
          compact={compact}
        />
      ))}
      {(hidden > 0 || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            // La píldora vive dentro de una fila que abre el detalle al hacer clic.
            e.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="px-3 py-0.5 text-[11px] font-bold text-emerald-700 hover:underline dark:text-emerald-400"
        >
          {expanded ? "Ver menos" : `Ver ${hidden} más`}
        </button>
      )}
    </div>
  );
}
